/**
 * Per-kind in-card controls. Every control calls the app's EXISTING write for
 * that thing (reconciliation RPCs, confirmOffset, admin_resync_booking_to_room,
 * the inbox's ticket writes, the approve/reopen/details/invite writes) —
 * resolving is never a Brain-owned write; only "last emailed" is Brain's own.
 * After a success the deck refreshes and advances; after a failure the card
 * stays.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { defaultFilter } from "cmdk";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useLanguage } from "@/contexts/LanguageContext";
import { isUnderMinimumAge, latestAdultBirthDate } from "@/lib/adult-age";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";
import { useAdminRoster, adminDisplayName } from "@/hooks/useAdminRoster";
import { useBrainState } from "@/hooks/brain/useBrainState";
import { useUpdateBooking, useUpdateProfile } from "@/hooks/v2/useProperties";
import {
  useOpenInvoices,
  useReconciliationMutations,
  useReconciliationReviewQueue,
  type OpenInvoice,
} from "@/hooks/v2/useReconciliation";
import {
  ConfirmAssignmentDialog,
  describeConfirmBlock,
  planConfirm,
} from "@/components/admin/v2/ReconciliationConfirmDialog";
import { BRAIN_POLL } from "@/lib/brain/thresholds";
import { useManagementInvoiceMutations } from "@/hooks/v2/useManagementInvoices";
import { useResyncBookingToRoom } from "@/hooks/v2/useReassignBookings";
import {
  fetchOpenRemainderNo,
  notifyRemainderOpened,
} from "@/hooks/v2/offset-remainder-queries";
import { logOwnerAudit } from "@/lib/owner-audit";
import { inviteOwnerToProperty } from "@/lib/admin-account-actions";
import {
  approveBookingForContract,
  formatContractEdgeError,
} from "@/lib/adminCreateContract";
import {
  reopenProtocolFromBrain,
  type ProtocolKind,
} from "@/lib/protocol-reopen";
import { openTenantDocument } from "@/lib/tenant-document-url";
import { offsetPayoutNote } from "@/lib/management-invoice-lines";
import { isLiveOffsetInvoice } from "@/lib/offset-remainder";
import { payoutToRecordCentsByInvoiceId } from "@/lib/owner-invoice-net-payout";
import type { OwnerPayoutGroup } from "@/lib/owner-payouts";
import { describeResync } from "@/lib/room-base-drift";
import { splitSummary, type BookedTransferSplit } from "@/lib/transfer-split";
import {
  archiveTickets,
  assignWarnings,
  buildAssignComment,
  setTicketStatus,
} from "@/lib/ticket-actions";
import { assignTicket } from "@/lib/ticket-assignment";
import { TicketDetail } from "@/components/admin/support/TicketDetail";
import { formatDate, formatEUR } from "@/lib/v2-format";
import { withBrainReturn } from "@/lib/brain/deck";
import { formatBrainMonth } from "@/lib/brain/time";
import { EMAIL_KINDS, type EmailKind } from "@/lib/brain/email-templates";
import type { DeckCard } from "@/lib/brain/types";
import type { ManagementInvoice } from "@/types/billing";
import { CardPrimaryButton } from "./FilledAction";
import { BrainEmailComposer } from "./BrainEmailComposer";
import { useBrainRun } from "./useBrainRun";
import { ConfirmAction } from "./ConfirmAction";
import { useBrainRefresh } from "@/hooks/brain/useBrainSources";
import { formatDateNumeric } from "@/lib/date-format";
import { approveMoveOutRequestFromBrain } from "@/lib/move-out-request-actions";
import { MoveOutRejectComposer } from "@/components/admin/v2/tenant/MoveOutRejectComposer";

type T = (key: string, fallback?: string) => string;
interface ActionProps {
  card: DeckCard;
  onDone(): void;
}

const SELECT_CLASS =
  "flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm";

/** What the RPC actually booked — the review page's toast, never a prediction. */
function bookedToast(
  booked: BookedTransferSplit | null,
  t: T,
  language: string
): string {
  const summary = booked ? splitSummary(booked, "EUR", t, language) : "";
  return summary
    ? t(
        "admin.reconciliation.split.toastApplied",
        "Transfer applied — {summary}"
      ).replace("{summary}", summary)
    : t("admin.reconciliation.split.toastAppliedPlain", "Transfer applied.");
}

const isEmailKind = (kind: string): kind is EmailKind =>
  (EMAIL_KINDS as readonly string[]).includes(kind);

export function BrainCardActions(props: ActionProps) {
  const { card } = props;
  const hasBooking = card.refs.bookingId != null;
  let body: JSX.Element | null = null;
  if (card.kind === "reconcile" && card.recon)
    body = <ReconcileActions {...props} />;
  else if (card.kind === "payout_ready" && card.payout)
    body = <PayoutActions {...props} />;
  else if (card.kind === "room_base_drift" && card.drift)
    body = <DriftActions {...props} />;
  else if (
    (card.kind === "ticket_unassigned" || card.kind === "ticket_stale") &&
    card.refs.ticketId
  )
    body = <TicketActions {...props} />;
  else if (card.kind === "application_review")
    body = (
      <>
        <DocumentList card={card} />
        {hasBooking && <ApproveAction {...props} />}
        <EmailAction {...props} />
      </>
    );
  else if (card.kind === "stage_handover_pending")
    body = (
      <>
        <EmailAction {...props} />
        {hasBooking && <ReopenAction {...props} kind="move_in" />}
      </>
    );
  else if (card.kind === "stage_moveout_protocol_pending" && hasBooking)
    body = <ReopenAction {...props} kind="move_out" />;
  else if (
    card.kind === "move_out_requested" &&
    card.moveOutRequest &&
    hasBooking
  )
    body = <MoveOutRequestedActions {...props} />;
  else if (card.kind === "tenant_details_missing" && hasBooking)
    body = <DetailsAction {...props} />;
  else if (
    card.kind === "owner_invite_unsigned" &&
    card.invitedProperties?.length
  )
    body = <InviteAction {...props} />;
  else if (isEmailKind(card.kind)) body = <EmailAction {...props} />;
  if (!body) return null;
  return (
    <div className="mt-3 space-y-2 border-t pt-3">
      <LastContact card={card} />
      {body}
    </div>
  );
}

/** When colivIQ last emailed this person, and when the automatic reminder did. */
function LastContact({ card }: { card: DeckCard }) {
  const { t, language } = useLanguage();
  const lines: [string, string, string | null | undefined][] = [
    ["lastEmailed", "Last emailed from colivIQ", card.lastEmailedAt],
    ["lastReminded", "Last automatic reminder", card.lastRemindedAt],
  ];
  return (
    <>
      {lines.map(([key, fallback, at]) =>
        at ? (
          <p key={key} className="text-xs text-muted-foreground">
            {t(`brain.actions.${key}`, fallback)}: {formatDate(at) ?? at}
          </p>
        ) : null
      )}
    </>
  );
}

function invoiceLabel(inv: OpenInvoice, t: T): string {
  const room = inv.bookings?.rooms;
  const where = [room?.properties?.title, room?.title]
    .filter(Boolean)
    .join(" · ");
  const tenant =
    inv.tenantName ||
    inv.tenantEmail ||
    t("admin.reconciliation.unknownTenant", "Unknown tenant");
  return `${tenant} — ${where || "—"} — ${inv.due_date?.slice(0, 7) ?? "—"} · ${formatEUR(inv.amount_cents / 100) ?? "—"}`;
}

function ReconcileActions({ card, onDone }: ActionProps) {
  const recon = card.recon!;
  const { t, language } = useLanguage();
  const { run, busy } = useBrainRun(onDone);
  const { confirm, reject, assign } = useReconciliationMutations();
  const [mode, setMode] = useState<"reject" | null>(null);
  const [reason, setReason] = useState("");
  const [search, setSearch] = useState("");
  const [paymentId, setPaymentId] = useState("");
  // Confirm books only from the review page's confirm dialog.
  const [confirming, setConfirming] = useState(false);
  const {
    data: invoices = [],
    isLoading: invoicesLoading,
    isError: invoicesFailed,
    // Fetch open invoices only once the admin actually searches (or confirms) —
    // the search box is always on the card, the network is not.
  } = useOpenInvoices(search.trim().length > 0 || confirming);
  // The deck's own queue read (same cache entry), resolved live like the
  // page does: a row another admin decided meanwhile closes the dialog.
  const { data: queue } = useReconciliationReviewQueue(true, BRAIN_POLL);
  const row = confirming
    ? (queue?.find(
        (r) => r.id === recon.matchId && r.status === "needs_review"
      ) ?? null)
    : null;
  // Fee-portion routing (COL-417) stays on the full page: the rule decides.
  const plan = row ? planConfirm(row, invoices, null) : null;
  const blockedReason = row
    ? describeConfirmBlock(row, plan, invoicesFailed, t)
    : null;
  // Fuzzy-ranked suggestions (cmdk's scorer); the spelled-out month is a
  // keyword so "juli" or "july" finds a 2026-07 invoice.
  const needle = search.trim();
  const labelled = invoices.map((inv) => ({
    id: inv.id,
    label: invoiceLabel(inv, t),
    month: formatBrainMonth(inv.due_date?.slice(0, 7) ?? "", language),
  }));
  const chosenLabel = labelled.find((o) => o.id === paymentId)?.label;
  const options = labelled
    .map((o) => ({
      ...o,
      score: needle ? defaultFilter(o.label, needle, [o.month]) : 1,
    }))
    .filter((o) => o.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, 8);

  return (
    <>
      <div className="flex flex-wrap gap-2">
        {recon.suggestedPaymentId && (
          <CardPrimaryButton
            size="sm"
            disabled={busy}
            onClick={() => setConfirming(true)}
          >
            {t("brain.actions.recon.confirm", "Confirm")}
          </CardPrimaryButton>
        )}
        <Button
          size="sm"
          variant="outline"
          aria-expanded={mode === "reject"}
          onClick={() => setMode(mode === "reject" ? null : "reject")}
        >
          {t("brain.actions.recon.reject", "Reject")}
        </Button>
      </div>
      {mode === "reject" && (
        <div className="flex gap-2">
          <Input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            aria-label={t("brain.actions.recon.reason", "Reason")}
            placeholder={t(
              "brain.actions.recon.reasonPlaceholder",
              "Why is this not a match?"
            )}
          />
          <ConfirmAction
            title={t("brain.confirm.recon.reject", "Reject this match?")}
            description={`${t("brain.actions.recon.reason", "Reason")}: ${reason.trim()}`}
            confirmLabel={t("brain.actions.recon.rejectSubmit", "Reject match")}
            onConfirm={() =>
              void run(async () => {
                await reject.mutateAsync({
                  matchId: recon.matchId,
                  reason: reason.trim(),
                });
                toast.success(
                  t(
                    "admin.reconciliation.toast.matchRejected",
                    "Match rejected."
                  )
                );
              })
            }
          >
            <Button
              size="sm"
              variant="destructive"
              disabled={busy || !reason.trim()}
            >
              {t("brain.actions.recon.rejectSubmit", "Reject match")}
            </Button>
          </ConfirmAction>
        </div>
      )}
      <div className="space-y-2">
        <div className="flex gap-2">
          <Input
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPaymentId("");
            }}
            aria-label={t("brain.actions.recon.search", "Search invoices")}
            placeholder={t(
              "brain.actions.recon.searchPlaceholder",
              "Tenant, property, month…"
            )}
          />
          <ConfirmAction
            title={t(
              "brain.confirm.recon.assign",
              "Assign the payment to this invoice?"
            )}
            description={chosenLabel}
            confirmLabel={t("brain.actions.recon.assignSubmit", "Assign")}
            onConfirm={() =>
              void run(async () => {
                const booked = await assign.mutateAsync({
                  matchId: recon.matchId,
                  paymentId,
                });
                toast.success(bookedToast(booked, t, language));
              })
            }
          >
            <CardPrimaryButton size="sm" disabled={busy || !paymentId}>
              {t("brain.actions.recon.assignSubmit", "Assign")}
            </CardPrimaryButton>
          </ConfirmAction>
        </div>
        {needle && !paymentId && (
          <ul
            role="listbox"
            aria-label={t("brain.actions.recon.invoice", "Invoice")}
            className="max-h-64 overflow-y-auto rounded-md border"
          >
            {options.map((o) => (
              <li
                key={o.id}
                role="option"
                aria-selected={false}
                tabIndex={0}
                className="cursor-pointer px-3 py-2 text-sm hover:bg-muted focus:bg-muted focus:outline-none"
                onClick={() => {
                  setPaymentId(o.id);
                  setSearch(o.label);
                }}
                onKeyDown={(e) => {
                  if (e.key !== "Enter" && e.key !== " ") return;
                  e.preventDefault();
                  setPaymentId(o.id);
                  setSearch(o.label);
                }}
              >
                {o.label}
              </li>
            ))}
            {options.length === 0 && (
              <li className="px-3 py-2 text-sm text-muted-foreground">
                {invoicesLoading
                  ? t(
                      "brain.actions.recon.loadingInvoices",
                      "Loading open invoices…"
                    )
                  : t(
                      "brain.actions.recon.noMatches",
                      "No open invoice matches"
                    )}
              </li>
            )}
          </ul>
        )}
      </div>
      <ConfirmAssignmentDialog
        row={row}
        split={plan?.split ?? null}
        blockedReason={blockedReason}
        blocked={!!blockedReason || invoicesLoading}
        pending={busy}
        onCancel={() => setConfirming(false)}
        onConfirm={() =>
          run(async () => {
            const booked = await confirm.mutateAsync({
              matchId: recon.matchId,
            });
            toast.success(bookedToast(booked, t, language));
          })
        }
      />
    </>
  );
}

/**
 * Checkout protocol (Neel Slack DM 2026-10-19): the colivIQ Brain half of
 * the tenant-requested move-out flow. "Confirm" and "Reject & email" write
 * through the SAME functions the admin tenant profile's Move-out tab uses
 * (`src/lib/move-out-request-actions.ts`) and open the SAME reject composer
 * (`MoveOutRejectComposer`) — the two surfaces can't drift apart. Confirm is
 * a `CardPrimaryButton`, so the card's "Open" button falls back to outline
 * automatically (FilledAction.tsx) while it's on screen.
 */
function MoveOutRequestedActions({ card, onDone }: ActionProps) {
  const req = card.moveOutRequest!;
  const { t, language } = useLanguage();
  const { run, busy } = useBrainRun(onDone);
  const refresh = useBrainRefresh();
  const [rejectOpen, setRejectOpen] = useState(false);
  const dateLabel = formatDateNumeric(req.requestedDate, language);
  const tenantName = card.refs.personName || card.refs.email || "";

  return (
    <>
      <div className="flex flex-wrap gap-2">
        <ConfirmAction
          title={t(
            "admin.tenantTabs.moveOut.confirmTitle",
            "Confirm move-out on {date}?"
          ).replace("{date}", dateLabel)}
          description={t(
            "admin.tenantTabs.moveOut.confirmDesc",
            "Are you sure you want to confirm {name}'s requested move-out date?\n\nThis turns on the move-out sequence with {date} as the confirmed date and sends {firstName} the move-out confirmation email immediately."
          )
            .replace("{name}", tenantName)
            .replace(/\{date\}/g, dateLabel)
            .replace("{firstName}", tenantName.split(" ")[0] || tenantName)}
          confirmLabel={t(
            "admin.tenantTabs.moveOut.confirmAction",
            "Yes, confirm move-out"
          )}
          onConfirm={() =>
            void run(async () => {
              const { requestRowUpdated } =
                await approveMoveOutRequestFromBrain({
                  bookingId: card.refs.bookingId!,
                  requestId: req.requestId,
                  requestedDate: req.requestedDate,
                  existingNoticeAt: req.existingNoticeAt,
                });
              if (!requestRowUpdated) {
                // Lost the claim race — nothing was confirmed by this call,
                // so showing the success toast too would lie about what
                // just happened.
                toast.warning(
                  t(
                    "admin.tenantTabs.moveOut.toastAlreadyDecided",
                    "This request was already decided elsewhere."
                  )
                );
                return;
              }
              toast.success(
                t(
                  "admin.tenantTabs.moveOut.tenantRequestConfirmed",
                  "Tenant request confirmed · {date} by you"
                ).replace(
                  "{date}",
                  formatDateNumeric(new Date().toISOString(), language)
                )
              );
            })
          }
        >
          <CardPrimaryButton size="sm" disabled={busy}>
            {t(
              "admin.tenantTabs.moveOut.confirmRequestedDate",
              "Confirm move-out"
            )}
          </CardPrimaryButton>
        </ConfirmAction>
        <Button
          size="sm"
          variant="outline"
          disabled={busy}
          onClick={() => setRejectOpen(true)}
        >
          {t("brain.moveOut.rejectAndEmail", "Reject & email")}
        </Button>
      </div>
      <MoveOutRejectComposer
        open={rejectOpen}
        onOpenChange={setRejectOpen}
        requestId={req.requestId}
        requestedDate={req.requestedDate}
        tenantEmail={card.refs.email || ""}
        tenantName={tenantName}
        onRejected={() => {
          void refresh();
          onDone();
        }}
      />
    </>
  );
}

function PayoutActions({ card, onDone }: ActionProps) {
  const p = card.payout!;
  const { t } = useLanguage();
  const { isSuperAdmin } = useAuth();
  const { run, busy } = useBrainRun(onDone);
  const { confirmOffset } = useManagementInvoiceMutations();
  const [amount, setAmount] = useState("");
  // null = untouched: shows the owner page's pre-filled note.
  const [note, setNote] = useState<string | null>(null);
  // The settlement invoice that records this payout. Under ["brain"] so the
  // deck refresh re-reads it. confirmOffset's WHERE guard and the DB's
  // super-admin trigger stay authoritative; this only finds the row.
  const invoices = useQuery({
    queryKey: [
      "brain",
      "payout-invoice",
      p.lessorId,
      p.propertyId,
      p.periodMonth,
    ],
    enabled: isSuperAdmin,
    queryFn: async (): Promise<ManagementInvoice[]> => {
      const { data, error } = await supabase
        .from("management_invoices")
        .select("*")
        .eq("lessor_id", p.lessorId)
        .eq("property_id", p.propertyId)
        .eq("period_month", `${p.periodMonth}-01`)
        .eq("settlement", "offset")
        .neq("status", "paid")
        .neq("status", "canceled");
      if (error) throw error;
      return (data ?? []) as ManagementInvoice[];
    },
  });
  const rows = invoices.data ?? [];
  // The owner page's own rule (COL-516): a figure is recorded only for the one
  // per-property invoice the payout group names as the one it pays out for.
  // Anything else — a stale group, a legacy per-apartment slice, an unknown
  // list — goes to the owner page. The cast: the function reads only these
  // four fields of the group.
  const figures = payoutToRecordCentsByInvoiceId(rows, [
    {
      propertyId: String(p.propertyId),
      periodMonth: p.periodMonth,
      livePayoutEur: p.payoutEur,
      unpaidInvoiceIds: p.unpaidInvoiceIds,
    } as OwnerPayoutGroup,
  ]);
  const row = rows.find((r) => figures.has(r.id)) ?? null;
  const figureCents = row ? (figures.get(row.id) ?? null) : null;
  const noteText = (note ?? (row ? offsetPayoutNote(row) : "")).trim();
  const typedCents = Math.round(Number(amount.trim().replace(",", ".")) * 100);
  const ownerLink = (
    <Link
      to={withBrainReturn(card.primaryHref, card.key)}
      className="text-primary hover:underline"
    >
      {t("brain.actions.payout.openOwner", "Open owner page")}
    </Link>
  );

  const markSent = (target: ManagementInvoice, netPayoutCents: number) =>
    run(async () => {
      await confirmOffset.mutateAsync({
        row: target,
        note: noteText,
        netPayoutCents,
      });
      // What the owner page does after the same write: the owner Activity
      // entry (best-effort), its toast, and the remainder notice.
      void logOwnerAudit({
        lessorId: target.lessor_id,
        action: "management_invoice_offset_confirmed",
        resourceType: "management_invoice",
        resourceId: String(target.id),
        metadata: { note: noteText },
      });
      toast.success(
        t(
          "admin.paymentsInvoiceActions.toastNetRentTransferred",
          "Net rent transferred — the fee is settled against the rent collected."
        )
      );
      // Only a live invoice's close opens a remainder.
      if (isLiveOffsetInvoice(target)) {
        void fetchOpenRemainderNo(target.id).then((no) => {
          if (no) notifyRemainderOpened(no, t);
        });
      }
    });

  return (
    <>
      <dl className="grid grid-cols-3 gap-x-4 gap-y-1 text-sm">
        {(
          [
            ["holder", "Account holder", p.bankHolder],
            ["iban", "IBAN", p.bankIban],
            ["bic", "BIC", p.bankBic],
          ] as const
        ).map(([key, fallback, value]) => (
          <div key={key}>
            <dt className="text-xs uppercase text-muted-foreground">
              {t(`brain.actions.payout.${key}`, fallback)}
            </dt>
            <dd className="break-all">{value || "—"}</dd>
          </div>
        ))}
      </dl>
      {isSuperAdmin &&
        (invoices.isLoading ? (
          <p className="text-sm text-muted-foreground">
            {t("brain.loading", "Loading…")}
          </p>
        ) : invoices.error ? (
          <p className="text-sm text-destructive">
            {t(
              "brain.actions.payout.loadFailed",
              "Couldn't load the management invoice."
            )}
          </p>
        ) : !row || figureCents === null ? (
          <div className="space-y-1 text-sm">
            <p>
              {rows.length === 0
                ? t(
                    "brain.actions.payout.noInvoice",
                    "Generate the management invoice on the owner page first"
                  )
                : t(
                    "brain.actions.payout.ownerPageOnly",
                    "This payout can't be recorded from here — record it on the owner page"
                  )}
            </p>
            {ownerLink}
          </div>
        ) : (
          <div className="space-y-2">
            <div className="flex gap-2">
              <Input
                inputMode="decimal"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                aria-label={t("brain.actions.payout.amount", "Amount sent (€)")}
                placeholder={(figureCents / 100).toFixed(2)}
              />
              <Input
                value={note ?? offsetPayoutNote(row)}
                onChange={(e) => setNote(e.target.value)}
                aria-label={t("brain.actions.payout.note", "Note")}
              />
            </div>
            <ConfirmAction
              title={t(
                "brain.confirm.payout.markSent",
                "Mark this payout as sent?"
              )}
              description={`${formatEUR(figureCents / 100)} → ${p.bankHolder || p.bankIban || "—"}`}
              confirmLabel={t("brain.actions.payout.markSent", "Mark as sent")}
              onConfirm={() => void markSent(row, figureCents)}
            >
              <CardPrimaryButton
                size="sm"
                disabled={
                  busy ||
                  !noteText ||
                  !amount.trim() ||
                  typedCents !== figureCents
                }
              >
                {t("brain.actions.payout.markSent", "Mark as sent")}
              </CardPrimaryButton>
            </ConfirmAction>
          </div>
        ))}
    </>
  );
}

function DriftActions({ card, onDone }: ActionProps) {
  const d = card.drift!;
  const { t } = useLanguage();
  const { isSuperAdmin } = useAuth();
  const { run, busy } = useBrainRun(onDone);
  const resync = useResyncBookingToRoom();
  const bookingId = card.refs.bookingId;
  // Only the figures that drifted carry a room value.
  const rows: [string, string, number | null, number][] = [];
  if (d.roomRent != null)
    rows.push(["rent", "Rent", d.bookingRent, d.roomRent]);
  if (d.roomDeposit != null)
    rows.push(["deposit", "Deposit", d.bookingDeposit, d.roomDeposit]);

  return (
    <>
      <table className="w-full text-sm">
        <thead className="text-xs uppercase text-muted-foreground">
          <tr>
            <th />
            <th className="text-left font-normal">
              {t("brain.actions.drift.booking", "On the booking")}
            </th>
            <th className="text-left font-normal">
              {t("brain.actions.drift.room", "Room")}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map(([key, fallback, booking, room]) => (
            <tr key={key}>
              <th scope="row" className="text-left font-medium">
                {t(`brain.actions.drift.${key}`, fallback)}
              </th>
              <td>{formatEUR(booking) ?? "—"}</td>
              <td>{formatEUR(room) ?? "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {isSuperAdmin && bookingId != null && (
        <ConfirmAction
          title={t("brain.confirm.drift.sync", "Sync the booking to the room?")}
          description={t(
            "brain.confirm.drift.syncDescription",
            "The booking takes the room's figures shown above."
          )}
          confirmLabel={t("brain.actions.drift.sync", "Sync to room")}
          onConfirm={() =>
            void run(async () => {
              const result = await resync.mutateAsync(bookingId);
              // RoomBaseDriftNotice's wording; nothing synced is not "Synced".
              if (!result.synced) {
                toast.warning(describeResync(result));
                return false;
              }
              toast.success(
                t("brain.actions.drift.synced", "Synced to the room"),
                { description: describeResync(result) }
              );
            })
          }
        >
          <CardPrimaryButton size="sm" disabled={busy}>
            {t("brain.actions.drift.sync", "Sync to room")}
          </CardPrimaryButton>
        </ConfirmAction>
      )}
    </>
  );
}

/** Assigns the ticket itself (issues.assigned_to): ticket cards read their
 *  assignee from there, never from brain_item_state. */
export function useTicketAssign(
  ticketId: string | undefined,
  onDone: () => void
) {
  const { t } = useLanguage();
  const { user } = useAuth();
  const { roster } = useAdminRoster();
  const { run, busy } = useBrainRun(onDone);

  // Same arguments SupportInbox.handleAssign passes; assignTicket writes
  // issues.assigned_to, the system note and the assignee email.
  const assignTo = (assigneeId: string | null, note?: string) => {
    const assigneeName = adminDisplayName(
      roster.find((a) => a.id === assigneeId),
      t
    );
    return run(async () => {
      const result = await assignTicket(supabase, {
        ticketId: ticketId!,
        assigneeId,
        actorId: user?.id || "",
        systemComment: buildAssignComment(assigneeId, roster, t, user?.id),
        assigneeName,
        note,
      });
      if (!result.assigned) {
        throw new Error(
          result.errorMessage ||
            t("support.detail.assignFailed", "Failed to update assignee")
        );
      }
      // Committed either way; like the inbox, success only when nothing is missing.
      const warnings = assignWarnings(result, assigneeName, t);
      if (warnings.length) for (const w of warnings) toast.warning(w);
      else toast.success(t("support.detail.assignSuccess", "Assignee updated"));
    });
  };
  return { assignTo, run, busy };
}

function TicketActions({ card, onDone }: ActionProps) {
  const ticketId = card.refs.ticketId!;
  const { t } = useLanguage();
  const { assignTo, run, busy } = useTicketAssign(ticketId, onDone);

  if (card.kind === "ticket_stale") {
    return (
      <ConfirmAction
        title={t("brain.confirm.ticket.close", "Close this ticket?")}
        confirmLabel={t("brain.actions.ticket.close", "Close ticket")}
        onConfirm={() =>
          void run(async () => {
            await setTicketStatus(ticketId, "resolved");
            toast.success(t("brain.actions.ticket.closed", "Ticket closed"));
          })
        }
      >
        <CardPrimaryButton size="sm" disabled={busy}>
          {t("brain.actions.ticket.close", "Close ticket")}
        </CardPrimaryButton>
      </ConfirmAction>
    );
  }
  // The inbox's own ticket pane: ticket text, conversation and reply
  // composer. A first admin reply auto-assigns the replier (IssueChatThread),
  // which resolves this card on the next refresh.
  return (
    <div className="space-y-2">
      <div className="h-[34rem] overflow-hidden rounded-xl border">
        <TicketDetail
          ticketId={ticketId}
          onStatusChange={(status) =>
            run(async () => {
              await setTicketStatus(ticketId, status);
              toast.success(
                t("support.detail.statusUpdateSuccess", "Status updated")
              );
            })
          }
          onAssign={(userId) => assignTo(userId)}
        />
      </div>
      <ConfirmAction
        title={t("brain.confirm.ticket.archive", "Archive this ticket?")}
        confirmLabel={t("brain.actions.ticket.archive", "Archive")}
        onConfirm={() =>
          void run(async () => {
            await archiveTickets([ticketId]);
            toast.success(
              t("brain.actions.ticket.archived", "Ticket archived")
            );
          })
        }
      >
        <Button size="sm" variant="outline" disabled={busy}>
          {t("brain.actions.ticket.archive", "Archive")}
        </Button>
      </ConfirmAction>
    </div>
  );
}

const EMAIL_BUTTON: Record<EmailKind, [string, string]> = {
  stage_signature_pending: ["brain.actions.email.reminder", "Send reminder"],
  stage_deposit_pending: ["brain.actions.email.reminder", "Send reminder"],
  stage_handover_pending: ["brain.actions.email.reminder", "Send reminder"],
  application_review: ["brain.actions.email.applicant", "Email applicant"],
  application_incomplete: ["brain.actions.email.applicant", "Email applicant"],
  onboarding_review: ["brain.actions.email.owner", "Email owner"],
  owner_bank_missing: ["brain.actions.email.owner", "Email owner"],
};

function EmailAction({ card, onDone }: ActionProps) {
  const { t } = useLanguage();
  const { user } = useAuth();
  // The deck's own state hook: same query cache, so the card's "last
  // emailed" line updates from this write.
  const { markEmailed } = useBrainState(user?.id);
  const [open, setOpen] = useState(false);
  // Held here, not in the composer: closing and reopening the composer
  // remounts it, and must not offer the same email a second time.
  const [sent, setSent] = useState(false);
  const [key, fallback] = EMAIL_BUTTON[card.kind as EmailKind];
  return (
    <>
      <Button
        size="sm"
        variant="outline"
        aria-expanded={open && !sent}
        disabled={sent}
        onClick={() => setOpen((o) => !o)}
      >
        {t(key, fallback)}
      </Button>
      {sent ? (
        <p className="text-sm text-muted-foreground">
          {t("brain.email.sent", "Email sent")}
        </p>
      ) : (
        open && (
          <BrainEmailComposer
            card={card}
            onSent={() => {
              setSent(true);
              return markEmailed(card.key);
            }}
            onDone={onDone}
          />
        )
      )}
    </>
  );
}

function ReopenAction({
  card,
  onDone,
  kind,
}: ActionProps & { kind: ProtocolKind }) {
  const { t } = useLanguage();
  const { user } = useAuth();
  const { run, busy } = useBrainRun(onDone);
  return (
    <ConfirmAction
      title={t("brain.confirm.reopen", "Reopen the protocol?")}
      description={t(
        "brain.confirm.reopenDescription",
        "The tenant can complete it again."
      )}
      confirmLabel={t("brain.actions.reopen", "Reopen protocol")}
      onConfirm={() =>
        void run(async () => {
          await reopenProtocolFromBrain(
            card.refs.bookingId!,
            kind,
            user?.id ?? null
          );
          // The tenant profile's own toasts for the same write.
          toast.success(
            kind === "move_in"
              ? t(
                  "admin.tenantProfile.toastMoveInReopened",
                  "Move-in protocol reopened — the tenant can complete it again"
                )
              : t(
                  "admin.tenantProfile.toastMoveOutReopened",
                  "Move-out protocol reopened — the tenant can complete it again"
                )
          );
        })
      }
    >
      <Button size="sm" variant="outline" disabled={busy}>
        {t("brain.actions.reopen", "Reopen protocol")}
      </Button>
    </ConfirmAction>
  );
}

const DOC_TYPE: Record<string, [string, string]> = {
  id_passport: ["admin.tenants.docTypeIdPassport", "ID/Passport"],
  proof_of_income: ["admin.tenants.docTypeProofOfIncome", "Proof of Income"],
};

function DocumentList({ card }: { card: DeckCard }) {
  const { t } = useLanguage();
  const docs = card.documents ?? [];
  if (!docs.length) return null;
  const open = async (fileUrl: string) => {
    if (!(await openTenantDocument(fileUrl)))
      toast.error(
        t("brain.actions.docs.openFailed", "Couldn't open the document")
      );
  };
  return (
    <ul className="space-y-1 text-sm">
      {docs.map((d) => {
        const label = DOC_TYPE[d.type] ? t(...DOC_TYPE[d.type]) : d.type;
        return (
          <li
            key={d.fileUrl}
            className="flex items-center justify-between gap-2"
          >
            <span>{label}</span>
            <Button
              size="sm"
              variant="ghost"
              aria-label={`${t("brain.actions.docs.open", "Open")} ${label}`}
              onClick={() => void open(d.fileUrl)}
            >
              {t("brain.actions.docs.open", "Open")}
            </Button>
          </li>
        );
      })}
    </ul>
  );
}

function ApproveAction({ card, onDone }: ActionProps) {
  const { t } = useLanguage();
  const { run, busy } = useBrainRun(onDone);
  const approve = () =>
    run(async () => {
      const result = await approveBookingForContract(card.refs.bookingId!);
      if (result.draftCreated) {
        toast.success(
          t(
            "brain.actions.approve.draftCreated",
            "Approved. Contract draft created — not sent."
          )
        );
        return;
      }
      // Approved either way; the tenants list's warning for a failed draft.
      const { title, description } = formatContractEdgeError(result.error, t);
      toast.warning(
        t(
          "admin.tenants.bookingApprovedContractGenFailed",
          "Booking approved, but contract generation failed. You can generate it manually."
        ),
        { description: description ? `${title}\n${description}` : title }
      );
    });
  return (
    <ConfirmAction
      title={t("brain.actions.approve.title", "Approve this application?")}
      description={t(
        "brain.actions.approve.description",
        "The booking is confirmed and a contract draft is created for review. Nothing is sent to the tenant."
      )}
      confirmLabel={t("brain.actions.approve.confirm", "Approve")}
      onConfirm={() => void approve()}
    >
      <CardPrimaryButton size="sm" disabled={busy}>
        {t("brain.actions.approve.button", "Approve for contract")}
      </CardPrimaryButton>
    </ConfirmAction>
  );
}

/** resolveTenantInfoGaps' field ids → the input for each. */
const DETAIL_FIELDS = [
  ["name", "name", "Name", "text"],
  ["date of birth", "dateOfBirth", "Date of birth", "date"],
  ["email", "email", "Email", "email"],
  ["phone", "phone", "Phone", "tel"],
] as const;
type DetailField = (typeof DETAIL_FIELDS)[number][0];

function DetailsAction({ card, onDone }: ActionProps) {
  const { t } = useLanguage();
  const { run, busy } = useBrainRun(onDone);
  const { bookingId, userId } = card.refs;
  const updateProfile = useUpdateProfile(userId);
  const updateBooking = useUpdateBooking(bookingId);
  const [values, setValues] = useState<Partial<Record<DetailField, string>>>(
    {}
  );
  const missing = card.missingFields ?? [];
  // A date of birth lives on the profile; a guest booking has none.
  const guestDob = !userId && missing.includes("date of birth");
  const fields = DETAIL_FIELDS.filter(
    ([f]) => missing.includes(f) && !(f === "date of birth" && !userId)
  );
  const typed = (f: DetailField) =>
    fields.some(([x]) => x === f) ? (values[f] ?? "").trim() : "";
  // COL-530: gender, like the date of birth, is on the account only.
  const askGender = !!userId && missing.includes("gender");
  const [gender, setGender] = useState("");
  const genderLabel = (g: string) =>
    g === "female"
      ? t("admin.tenantEdit.genderFemale", "Woman")
      : t("admin.tenantEdit.genderMale", "Man");
  const anyTyped = fields.some(([f]) => typed(f)) || !!gender;

  // TenantEditPanel's split: contact on the profile AND mirrored onto the
  // booking; the date of birth on the profile only, stamped as typed in.
  const save = () => {
    // COL-629: nobody under 18 on a profile (the profiles trigger agrees).
    if (isUnderMinimumAge(typed("date of birth"))) {
      toast.error(
        t(
          "admin.tenantEdit.errDobUnderage",
          "The tenant must be at least 18 years old. Check the date of birth."
        )
      );
      return;
    }
    return run(async () => {
      const contact: { name?: string; email?: string; phone?: string } = {};
      for (const f of ["name", "email", "phone"] as const)
        if (typed(f)) contact[f] = typed(f);
      const dob = typed("date of birth");
      const tasks: Promise<unknown>[] = [];
      if (Object.keys(contact).length)
        tasks.push(updateBooking.mutateAsync(contact));
      if (userId && (Object.keys(contact).length || dob || gender))
        tasks.push(
          updateProfile.mutateAsync({
            ...contact,
            ...(dob && {
              date_of_birth: dob,
              date_of_birth_source: "manual",
            }),
            ...(gender && { gender }),
          })
        );
      await Promise.all(tasks);
      toast.success(t("brain.actions.details.saved", "Details saved"));
    });
  };

  return (
    <>
      {fields.map(([f, key, fallback, type]) => (
        <label key={f} className="block space-y-1 text-sm">
          <span>{t(`brain.actions.details.${key}`, fallback)}</span>
          <Input
            type={type}
            max={type === "date" ? latestAdultBirthDate() : undefined}
            value={values[f] ?? ""}
            onChange={(e) => setValues({ ...values, [f]: e.target.value })}
          />
        </label>
      ))}
      {askGender && (
        <label className="block space-y-1 text-sm">
          <span>{t("admin.tenantEdit.gender", "Gender")}</span>
          <select
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            value={gender}
            onChange={(e) => setGender(e.target.value)}
          >
            <option value="">
              {t("admin.tenantEdit.genderUnset", "Not specified")}
            </option>
            <option value="female">{genderLabel("female")}</option>
            <option value="male">{genderLabel("male")}</option>
          </select>
        </label>
      )}
      {guestDob && (
        <p className="text-sm text-muted-foreground">
          {t(
            "brain.actions.details.guestDob",
            "Guest booking — date of birth needs an account"
          )}
        </p>
      )}
      {(fields.length > 0 || askGender) && (
        <ConfirmAction
          title={t("brain.confirm.details.save", "Save these details?")}
          description={[
            ...fields
              .filter(([f]) => typed(f))
              .map(
                ([f, key, fallback]) =>
                  `${t(`brain.actions.details.${key}`, fallback)}: ${typed(f)}`
              ),
            ...(gender
              ? [
                  `${t("admin.tenantEdit.gender", "Gender")}: ${genderLabel(gender)}`,
                ]
              : []),
          ].join("\n")}
          confirmLabel={t("brain.actions.details.save", "Save details")}
          onConfirm={() => void save()}
        >
          <CardPrimaryButton size="sm" disabled={busy || !anyTyped}>
            {t("brain.actions.details.save", "Save details")}
          </CardPrimaryButton>
        </ConfirmAction>
      )}
    </>
  );
}

function InviteAction({ card, onDone }: ActionProps) {
  const { t } = useLanguage();
  const { run, busy } = useBrainRun(onDone);
  // Only property-wide invites go out from here; an apartment-scoped one
  // needs the owner page's per-apartment resend.
  const all = card.invitedProperties ?? [];
  const properties = all.filter((p) => p.resendable);
  const [picked, setPicked] = useState("");
  const email = card.refs.email?.trim();
  if (!email) {
    return (
      <p className="text-sm text-muted-foreground">
        {t("brain.email.noAddress", "No email address on file")}
      </p>
    );
  }
  const propertyId =
    properties.length === 1 ? properties[0].id : picked ? Number(picked) : null;
  const ownerPage = properties.length < all.length && (
    <Link
      to={withBrainReturn(card.primaryHref, card.key)}
      className="text-sm text-primary hover:underline"
    >
      {t("brain.actions.invite.ownerPage", "Resend from the owner page")}
    </Link>
  );
  if (!properties.length) return ownerPage || null;
  return (
    <>
      <div className="flex gap-2">
        {properties.length > 1 && (
          <select
            className={SELECT_CLASS}
            value={picked}
            onChange={(e) => setPicked(e.target.value)}
            aria-label={t("brain.actions.invite.property", "Property")}
          >
            <option value="">
              {t("brain.actions.invite.pickProperty", "Choose a property")}
            </option>
            {properties.map((p) => (
              <option key={p.id} value={p.id}>
                {p.title || `#${p.id}`}
              </option>
            ))}
          </select>
        )}
        <ConfirmAction
          title={t(
            "brain.confirm.invite.resend",
            "Resend the onboarding invite?"
          )}
          description={email}
          confirmLabel={t("brain.actions.invite.resend", "Resend invite")}
          onConfirm={() =>
            void run(async () => {
              const res = await inviteOwnerToProperty({
                email,
                propertyId: propertyId!,
                name: card.refs.personName,
              });
              if (!res.ok)
                throw new Error(
                  res.error ??
                    t(
                      "admin.ownerDetail.toastInvitationFailed",
                      "Could not send the onboarding invitation"
                    )
                );
              toast.success(
                t(
                  "admin.ownerDetail.toastInvitationSent",
                  "Onboarding invitation sent"
                )
              );
            })
          }
        >
          <CardPrimaryButton size="sm" disabled={busy || propertyId == null}>
            {t("brain.actions.invite.resend", "Resend invite")}
          </CardPrimaryButton>
        </ConfirmAction>
      </div>
      {ownerPage}
    </>
  );
}
